// AlertFollowupProposals.jsx
// DAS-2642: shows auto-DETECTED follow-up proposals (from alert_followup_matcher)
// and lets the owner CONFIRM or REJECT each. Nothing is recorded as "followed up"
// without a confirm — the matcher only proposes. Mounted at the top of the Alert
// Effectiveness modal (a live, rendered surface).
//
// API (shared axios `api` instance — no `/api` prefix; baseURL already has it):
//   GET  /alert_followup/proposals/:userId
//   POST /alert_followup/decide  { alert_id, userId, decision: 'confirm'|'reject' }

import React, { useEffect, useState } from 'react';
import { Button, Tag, Tooltip, message } from 'antd';
import api from '../functions/api';

const AlertFollowupProposals = ({ userId, isVisible }) => {
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState({});   // alert_id -> true while deciding

  useEffect(() => {
    let alive = true;
    if (!isVisible || !userId) return undefined;
    api.get(`/alert_followup/proposals/${userId}`)
      .then((res) => { if (alive) setProposals(res.data?.proposals || []); })
      .catch(() => { /* silent — panel just stays empty */ });
    return () => { alive = false; };
  }, [isVisible, userId]);

  const decide = async (alert_id, decision) => {
    setBusy((b) => ({ ...b, [alert_id]: true }));
    try {
      await api.post('/alert_followup/decide', { alert_id, userId, decision });
      setProposals((ps) => ps.filter((p) => p.alert_id !== alert_id));
      message.success(decision === 'confirm' ? 'Marked as followed up' : 'Dismissed');
    } catch (err) {
      message.error('Could not save: ' + (err?.response?.data?.error || err.message));
    } finally {
      setBusy((b) => ({ ...b, [alert_id]: false }));
    }
  };

  if (!proposals.length) return null;

  return (
    <div style={{
      border: '1px solid #91caff', background: '#e6f4ff', borderRadius: 8,
      padding: '10px 12px', marginBottom: 12,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        📌 Did you follow these up? ({proposals.length})
        <Tooltip title="Detected from your real transactions (matching ticker, direction and timing). Confirm to count it toward your follow-up rate, or dismiss if it wasn't a follow-up. Nothing is recorded until you decide.">
          <span style={{ marginLeft: 6, color: '#1677ff', cursor: 'help', fontWeight: 400, fontSize: 12 }}>ⓘ</span>
        </Tooltip>
      </div>
      {proposals.map((p) => (
        <div key={p.alert_id} style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '6px 0', borderTop: '1px solid #cfe6ff',
        }}>
          <b style={{ minWidth: 60 }}>{p.ticker}</b>
          <Tag>{p.alert_type}</Tag>
          <span style={{ flex: '1 1 240px', minWidth: 0, color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>
            {p.evidence}
          </span>
          {p.confidence != null && (
            <Tag color="blue">conf {Math.round(Number(p.confidence) * 100)}%</Tag>
          )}
          <Button size="small" type="primary" loading={!!busy[p.alert_id]}
            onClick={() => decide(p.alert_id, 'confirm')}>Yes, followed up</Button>
          <Button size="small" loading={!!busy[p.alert_id]}
            onClick={() => decide(p.alert_id, 'reject')}>No</Button>
        </div>
      ))}
    </div>
  );
};

export default AlertFollowupProposals;
